import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { quotationsAPI, projectsAPI, suppliersAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { QuotationFormDialog } from './QuotationFormDialog'
import { AddMaterialDialog } from './AddMaterialDialog'
import { AddSupplierDialog } from './AddSupplierDialog'
import {
  ArrowLeft,
  Plus,
  Loader2,
  Trash2,
  Scale,
  Package,
  Users,
  FolderOpen,
  CheckCircle2,
  Pencil,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Rascunho',
  OPEN: 'Aberto',
  APPROVED: 'Aprovado',
  CANCELLED: 'Cancelado',
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-neutral-100 text-neutral-600',
  OPEN: 'bg-blue-100 text-blue-700',
  APPROVED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-600',
}

function formatCurrency(value: number | string | null | undefined) {
  if (value == null || value === '') return '—'
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
}

// Converte centavos (inteiro) → "R$ 1.234,56"
function centsToBrl(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100)
}

// Valor numérico → string mascarada para exibição no input
function toMasked(value: number | string | null | undefined): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return ''
  return centsToBrl(Math.round(n * 100))
}

// String mascarada (ex: "R$ 1.234,56") → número (1234.56) ou null
function fromMasked(masked: string): number | null {
  const digits = masked.replace(/\D/g, '')
  if (!digits) return null
  return parseInt(digits, 10) / 100
}

// ─── Price Cell ──────────────────────────────────────────────────────────────

interface PriceCellProps {
  price: any // QuotationPrice row (may be undefined if not yet created)
  itemId: string
  quotationSupplierId: string
  quotationId: string
  isLowest: boolean
  canEdit: boolean
}

function PriceCell({ price, itemId, quotationSupplierId, quotationId, isLowest, canEdit }: PriceCellProps) {
  const queryClient = useQueryClient()
  const serverNum = price?.unitPrice != null ? parseFloat(price.unitPrice) : null
  const [inputVal, setInputVal] = useState<string>(() => toMasked(serverNum))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) setInputVal(toMasked(serverNum))
  }, [serverNum, isFocused])

  const priceMutation = useMutation({
    mutationFn: (data: { unitPrice?: number | null; isSelected?: boolean }) =>
      quotationsAPI.setPrice(quotationId, itemId, quotationSupplierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    setInputVal(digits ? centsToBrl(parseInt(digits, 10)) : '')
  }

  const handleBlur = () => {
    setIsFocused(false)
    const newPrice = fromMasked(inputVal)
    if (newPrice === serverNum) return
    priceMutation.mutate({ unitPrice: newPrice })
  }

  const handleSelectChange = (checked: boolean) => {
    priceMutation.mutate({ isSelected: checked })
  }

  const isSelected = price?.isSelected ?? false

  return (
    <td
      className={cn(
        'px-3 py-2 text-center align-middle border-l',
        isLowest && price?.unitPrice != null && 'bg-green-50',
        isSelected && 'bg-blue-50',
      )}
    >
      <div className="flex flex-col items-center gap-1">
        {canEdit ? (
          <Input
            type="text"
            inputMode="numeric"
            value={inputVal}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            placeholder="R$ —"
            className={cn(
              'h-7 w-32 text-center text-sm',
              isLowest && price?.unitPrice != null && 'border-green-400 font-semibold',
            )}
          />
        ) : (
          <span className={cn('text-sm', isLowest && price?.unitPrice != null && 'font-semibold text-green-700')}>
            {formatCurrency(price?.unitPrice)}
          </span>
        )}
        <Checkbox
          checked={isSelected}
          onChange={(e) => handleSelectChange(e.target.checked)}
          disabled={!canEdit || price?.unitPrice == null}
          className={cn(isSelected && 'border-blue-500 bg-blue-500')}
        />
      </div>
    </td>
  )
}

// ─── Material Name Cell ──────────────────────────────────────────────────────

interface MaterialNameCellProps {
  item: any
  quotationId: string
  canEdit: boolean
}

function MaterialNameCell({ item, quotationId, canEdit }: MaterialNameCellProps) {
  const queryClient = useQueryClient()
  const serverName = item.material.name as string
  const [inputVal, setInputVal] = useState(serverName)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) setInputVal(serverName)
  }, [serverName, isFocused])

  const updateMutation = useMutation({
    mutationFn: (name: string) => suppliersAPI.updateMaterial(item.materialId, { name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const handleBlur = () => {
    setIsFocused(false)
    const trimmed = inputVal.trim()
    if (!trimmed || trimmed === serverName) return
    updateMutation.mutate(trimmed)
  }

  if (!canEdit) {
    return <span className="truncate">{serverName}</span>
  }

  return (
    <Input
      type="text"
      value={inputVal}
      onChange={(e) => setInputVal(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      className="h-7 text-sm"
    />
  )
}

// ─── Quantity Cell ───────────────────────────────────────────────────────────

interface QuantityCellProps {
  item: any
  quotationId: string
  canEdit: boolean
}

function QuantityCell({ item, quotationId, canEdit }: QuantityCellProps) {
  const queryClient = useQueryClient()
  const serverVal = parseFloat(item.quantity)
  const [inputVal, setInputVal] = useState(() => serverVal.toLocaleString('pt-BR', { maximumFractionDigits: 3 }))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) setInputVal(serverVal.toLocaleString('pt-BR', { maximumFractionDigits: 3 }))
  }, [serverVal, isFocused])

  const updateMutation = useMutation({
    mutationFn: (quantity: number) =>
      quotationsAPI.updateMaterial(quotationId, item.id, { quantity }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const handleBlur = () => {
    setIsFocused(false)
    const n = parseFloat(inputVal.replace(',', '.'))
    if (isNaN(n) || n <= 0 || n === serverVal) return
    updateMutation.mutate(n)
  }

  if (!canEdit) {
    return <span>{serverVal.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}</span>
  }

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={inputVal}
      onChange={(e) => setInputVal(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      className="h-7 w-20 text-center text-sm"
    />
  )
}

// ─── Supplier Notes Cell ─────────────────────────────────────────────────────

interface SupplierNotesCellProps {
  qs: any // QuotationSupplier row
  quotationId: string
  canEdit: boolean
}

function SupplierNotesCell({ qs, quotationId, canEdit }: SupplierNotesCellProps) {
  const queryClient = useQueryClient()
  const serverNotes: string = qs.notes ?? ''
  const [inputVal, setInputVal] = useState(serverNotes)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (!isFocused) setInputVal(serverNotes)
  }, [serverNotes, isFocused])

  const updateMutation = useMutation({
    mutationFn: (notes: string) =>
      quotationsAPI.updateSupplier(quotationId, qs.id, { notes: notes || null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotation', quotationId] }),
    onError: (e: Error) => toast.error(e.message),
  })

  const handleBlur = () => {
    setIsFocused(false)
    if (inputVal === serverNotes) return
    updateMutation.mutate(inputVal)
  }

  if (!canEdit) {
    return <span className="text-xs text-neutral-500">{serverNotes || '—'}</span>
  }

  return (
    <textarea
      value={inputVal}
      onChange={(e) => setInputVal(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      rows={2}
      placeholder="Observações..."
      className="w-full resize-none rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
    />
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function QuotationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canEdit = usePermission('quotations:edit')
  const canApprove = usePermission('quotations:approve')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showAddMaterial, setShowAddMaterial] = useState(false)
  const [showAddSupplier, setShowAddSupplier] = useState(false)
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [approveProjectId, setApproveProjectId] = useState('')
  const [removingItemId, setRemovingItemId] = useState<string | null>(null)
  const [removingSupplierId, setRemovingSupplierId] = useState<string | null>(null)

  const { data: qr, isLoading } = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => quotationsAPI.getById(id!),
    enabled: !!id,
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: showApproveDialog && !qr?.projectId,
  })
  const projects = projectsData?.data || []

  const removeMaterialMutation = useMutation({
    mutationFn: (itemId: string) => quotationsAPI.removeMaterial(id!, itemId),
    onSuccess: () => {
      toast.success('Material removido.')
      queryClient.invalidateQueries({ queryKey: ['quotation', id] })
      setRemovingItemId(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const removeSupplierMutation = useMutation({
    mutationFn: (qsId: string) => quotationsAPI.removeSupplier(id!, qsId),
    onSuccess: () => {
      toast.success('Fornecedor removido.')
      queryClient.invalidateQueries({ queryKey: ['quotation', id] })
      setRemovingSupplierId(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const approveMutation = useMutation({
    mutationFn: () =>
      quotationsAPI.approve(id!, {
        projectId: approveProjectId || qr?.projectId || undefined,
      }),
    onSuccess: (result: any) => {
      const count = result.purchaseOrders?.length ?? 0
      toast.success(`QC aprovado! ${count} Pedido(s) de Compra gerado(s).`)
      queryClient.invalidateQueries({ queryKey: ['quotation', id] })
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      setShowApproveDialog(false)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // Build price map: priceMap[itemId][quotationSupplierId] = QuotationPrice
  const priceMap = useMemo(() => {
    if (!qr) return {}
    const map: Record<string, Record<string, any>> = {}
    for (const item of qr.items ?? []) {
      map[item.id] = {}
      for (const p of item.prices ?? []) {
        map[item.id][p.quotationSupplierId] = p
      }
    }
    return map
  }, [qr])

  // Lowest price per item row (across all suppliers)
  const lowestPriceMap = useMemo(() => {
    if (!qr) return {}
    const result: Record<string, number | null> = {}
    for (const item of qr.items ?? []) {
      const prices = (item.prices ?? [])
        .map((p: any) => (p.unitPrice != null ? parseFloat(p.unitPrice) : null))
        .filter((v: number | null): v is number => v !== null)
      result[item.id] = prices.length > 0 ? Math.min(...prices) : null
    }
    return result
  }, [qr])

  // Total selected per supplier column
  const supplierTotals = useMemo(() => {
    if (!qr) return {}
    const totals: Record<string, number> = {}
    for (const qs of qr.suppliers ?? []) {
      let total = 0
      for (const item of qr.items ?? []) {
        const p = priceMap[item.id]?.[qs.id]
        if (p?.isSelected && p?.unitPrice != null) {
          total += parseFloat(p.unitPrice) * parseFloat(item.quantity)
        }
      }
      totals[qs.id] = total
    }
    return totals
  }, [qr, priceMap])

  // Summary: per supplier with at least 1 selected item
  const summaryBySupplier = useMemo(() => {
    if (!qr) return []
    return (qr.suppliers ?? []).flatMap((qs: any) => {
      const lines = (qr.items ?? []).flatMap((item: any) => {
        const p = priceMap[item.id]?.[qs.id]
        if (!p?.isSelected || p?.unitPrice == null) return []
        const unitPrice = parseFloat(p.unitPrice)
        const quantity = parseFloat(item.quantity)
        return [{
          materialName: item.material.name,
          unit: item.material.unit,
          quantity,
          unitPrice,
          lineTotal: unitPrice * quantity,
        }]
      })
      if (lines.length === 0) return []
      const subtotal = lines.reduce((s: number, l: any) => s + l.lineTotal, 0)
      return [{ supplierName: qs.supplier.name, lines, subtotal }]
    })
  }, [qr, priceMap])

  const grandTotal = useMemo(
    () => summaryBySupplier.reduce((s: number, g: any) => s + g.subtotal, 0),
    [summaryBySupplier],
  )

  // Items without any selected winner
  const itemsWithoutWinner = useMemo(() => {
    if (!qr) return []
    return (qr.items ?? []).filter((item: any) =>
      !(item.prices ?? []).some((p: any) => p.isSelected),
    )
  }, [qr])

  const existingMaterialIds = useMemo(
    () => (qr?.items ?? []).map((i: any) => i.materialId),
    [qr],
  )
  const existingSupplierIds = useMemo(
    () => (qr?.suppliers ?? []).map((s: any) => s.supplierId),
    [qr],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!qr) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <Scale className="h-16 w-16 text-neutral-300" />
        <p className="text-neutral-500">Quadro de Concorrência não encontrado.</p>
        <Button variant="outline" onClick={() => navigate('/quotations')}>
          <ArrowLeft className="mr-2 h-4 w-4" />Voltar
        </Button>
      </div>
    )
  }

  const isApproved = qr.status === 'APPROVED' || qr.status === 'CANCELLED'
  const items: any[] = qr.items ?? []
  const suppliers: any[] = qr.suppliers ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/quotations')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-neutral-900 truncate">{qr.title}</h1>
              <Badge variant="outline" className={`shrink-0 ${STATUS_COLORS[qr.status] || ''}`}>
                {STATUS_LABELS[qr.status] || qr.status}
              </Badge>
            </div>
            {qr.project && (
              <div className="mt-1 flex items-center gap-1.5 text-sm text-neutral-500">
                <FolderOpen className="h-3.5 w-3.5" />
                <span>{qr.project.name}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <Button variant="outline" size="icon" onClick={() => setShowEditDialog(true)}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Empty state */}
      {items.length === 0 && suppliers.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16 gap-4">
          <Scale className="h-16 w-16 text-neutral-300" />
          <h3 className="text-lg font-medium text-neutral-700">Quadro vazio</h3>
          <p className="text-sm text-neutral-500 text-center max-w-xs">
            Adicione materiais do catálogo e fornecedores para montar o quadro comparativo.
          </p>
          {canEdit && !isApproved && (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowAddMaterial(true)}>
                <Package className="mr-2 h-4 w-4" />Adicionar Material
              </Button>
              <Button variant="outline" onClick={() => setShowAddSupplier(true)}>
                <Users className="mr-2 h-4 w-4" />Adicionar Fornecedor
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Matrix table */}
      {(items.length > 0 || suppliers.length > 0) && (
        <div className="rounded-lg border bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-neutral-50 border-b">
                  <th className="px-3 py-2.5 text-left font-medium text-neutral-600 w-[260px]">
                    Material
                  </th>
                  <th className="px-3 py-2.5 text-center font-medium text-neutral-600 w-16">
                    Un.
                  </th>
                  <th className="px-3 py-2.5 text-center font-medium text-neutral-600 w-20">
                    Qtde
                  </th>
                  {suppliers.map((qs: any) => (
                    <th key={qs.id} className="px-3 py-2.5 text-center font-medium text-neutral-600 border-l min-w-[160px]">
                      <div className="flex items-center justify-center gap-1">
                        <span className="truncate max-w-[120px]">{qs.supplier.name}</span>
                        {canEdit && !isApproved && (
                          <button
                            type="button"
                            onClick={() => setRemovingSupplierId(qs.id)}
                            className="ml-1 shrink-0 text-neutral-300 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                  {canEdit && !isApproved && (
                    <th className="px-3 py-2.5 border-l w-12">
                      <button
                        type="button"
                        onClick={() => setShowAddSupplier(true)}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium whitespace-nowrap"
                      >
                        <Plus className="h-3.5 w-3.5" />Fornecedor
                      </button>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y">
                {items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3 + suppliers.length + (canEdit && !isApproved ? 1 : 0)}
                      className="px-3 py-8 text-center text-neutral-400"
                    >
                      Nenhum material adicionado
                    </td>
                  </tr>
                ) : (
                  items.map((item: any) => {
                    const lowestPrice = lowestPriceMap[item.id]
                    return (
                      <tr key={item.id} className="hover:bg-neutral-50/50">
                        <td className="px-3 py-2 font-medium">
                          <div className="flex items-center justify-between gap-2">
                            <MaterialNameCell item={item} quotationId={id!} canEdit={canEdit && !isApproved} />
                            {canEdit && !isApproved && (
                              <button
                                type="button"
                                onClick={() => setRemovingItemId(item.id)}
                                className="shrink-0 text-neutral-300 hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-500">
                          {item.material.unit}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-700">
                          <QuantityCell item={item} quotationId={id!} canEdit={canEdit && !isApproved} />
                        </td>
                        {suppliers.map((qs: any) => {
                          const p = priceMap[item.id]?.[qs.id]
                          const unitPrice =
                            p?.unitPrice != null ? parseFloat(p.unitPrice) : null
                          return (
                            <PriceCell
                              key={qs.id}
                              price={p}
                              itemId={item.id}
                              quotationSupplierId={qs.id}
                              quotationId={id!}
                              isLowest={lowestPrice !== null && unitPrice === lowestPrice}
                              canEdit={canEdit && !isApproved}
                            />
                          )
                        })}
                        {canEdit && !isApproved && <td className="border-l" />}
                      </tr>
                    )
                  })
                )}
              </tbody>

              {/* Footer: totals + supplier notes */}
              {suppliers.length > 0 && items.length > 0 && (
                <tfoot>
                  <tr className="bg-neutral-50 border-t font-semibold">
                    <td className="px-3 py-2.5 text-neutral-600" colSpan={3}>
                      Total selecionado
                    </td>
                    {suppliers.map((qs: any) => (
                      <td key={qs.id} className="px-3 py-2.5 text-center border-l">
                        <span
                          className={cn(
                            'text-sm',
                            supplierTotals[qs.id] > 0
                              ? 'text-blue-700'
                              : 'text-neutral-400',
                          )}
                        >
                          {supplierTotals[qs.id] > 0
                            ? formatCurrency(supplierTotals[qs.id])
                            : '—'}
                        </span>
                      </td>
                    ))}
                    {canEdit && !isApproved && <td className="border-l" />}
                  </tr>
                  <tr className="border-t-2 border-neutral-200">
                    <td className="px-3 py-2 text-xs text-neutral-500 font-medium" colSpan={3}>
                      Inf. complementares
                    </td>
                    {suppliers.map((qs: any) => (
                      <td key={qs.id} className="px-3 py-2 border-l align-top">
                        <SupplierNotesCell
                          qs={qs}
                          quotationId={id!}
                          canEdit={canEdit && !isApproved}
                        />
                      </td>
                    ))}
                    {canEdit && !isApproved && <td className="border-l" />}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Add material row */}
          {canEdit && !isApproved && (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                onClick={() => setShowAddMaterial(true)}
                className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
              >
                <Plus className="h-4 w-4" />
                Adicionar material
              </button>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      {items.length > 0 && suppliers.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-neutral-500">
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-green-100 border border-green-300" />
            <span>Menor preço</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded bg-blue-100 border border-blue-300" />
            <span>Selecionado (vencedor)</span>
          </div>
        </div>
      )}

      {/* Summary */}
      {summaryBySupplier.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-neutral-900">Resumo do Orçamento</h2>
          </div>

          {/* Itens sem vencedor */}
          {itemsWithoutWinner.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="font-medium">Sem vencedor definido:</span>{' '}
              {itemsWithoutWinner.map((i: any) => i.material.name).join(', ')}
            </div>
          )}

          {/* Card por fornecedor */}
          {summaryBySupplier.map((group: any) => (
            <div key={group.supplierName} className="rounded-lg border bg-white overflow-hidden">
              <div className="bg-neutral-50 border-b px-4 py-2.5 flex items-center justify-between">
                <span className="font-semibold text-neutral-800">{group.supplierName}</span>
                <span className="text-sm font-semibold text-primary">
                  {formatCurrency(group.subtotal)}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-neutral-500 text-xs">
                    <th className="px-4 py-2 text-left font-medium">Material</th>
                    <th className="px-4 py-2 text-center font-medium w-16">Un.</th>
                    <th className="px-4 py-2 text-center font-medium w-20">Qtde</th>
                    <th className="px-4 py-2 text-right font-medium w-28">Preço unit.</th>
                    <th className="px-4 py-2 text-right font-medium w-32">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {group.lines.map((line: any, idx: number) => (
                    <tr key={idx} className="hover:bg-neutral-50/50">
                      <td className="px-4 py-2.5">{line.materialName}</td>
                      <td className="px-4 py-2.5 text-center text-neutral-500">{line.unit}</td>
                      <td className="px-4 py-2.5 text-center">
                        {line.quantity.toLocaleString('pt-BR')}
                      </td>
                      <td className="px-4 py-2.5 text-right text-neutral-700">
                        {formatCurrency(line.unitPrice)}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium">
                        {formatCurrency(line.lineTotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Grand total + approve */}
          <div className="rounded-lg border bg-neutral-50 px-4 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-neutral-500">Total geral</p>
              <p className="text-2xl font-bold text-neutral-900">{formatCurrency(grandTotal)}</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                {summaryBySupplier.length} fornecedor{summaryBySupplier.length !== 1 ? 'es' : ''} ·{' '}
                {summaryBySupplier.reduce((s: number, g: any) => s + g.lines.length, 0)} item(ns)
              </p>
            </div>
            {canApprove && !isApproved && (
              <Button size="lg" onClick={() => setShowApproveDialog(true)}>
                <CheckCircle2 className="mr-2 h-5 w-5" />
                Aprovar e Gerar Pedidos
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Dialogs */}
      <QuotationFormDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        quotation={qr}
      />

      <AddMaterialDialog
        open={showAddMaterial}
        onOpenChange={setShowAddMaterial}
        quotationId={id!}
        existingMaterialIds={existingMaterialIds}
      />

      <AddSupplierDialog
        open={showAddSupplier}
        onOpenChange={setShowAddSupplier}
        quotationId={id!}
        existingSupplierIds={existingSupplierIds}
      />

      {/* Remove material confirm */}
      <Dialog open={!!removingItemId} onOpenChange={(o) => !o && setRemovingItemId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remover material</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover este material? Todos os preços cadastrados para ele serão excluídos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemovingItemId(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={removeMaterialMutation.isPending}
              onClick={() => removingItemId && removeMaterialMutation.mutate(removingItemId)}
            >
              {removeMaterialMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove supplier confirm */}
      <Dialog open={!!removingSupplierId} onOpenChange={(o) => !o && setRemovingSupplierId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remover fornecedor</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover este fornecedor? Todos os preços cadastrados por ele serão excluídos.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemovingSupplierId(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={removeSupplierMutation.isPending}
              onClick={() => removingSupplierId && removeSupplierMutation.mutate(removingSupplierId)}
            >
              {removeSupplierMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={showApproveDialog} onOpenChange={(o) => !o && setShowApproveDialog(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Aprovar e Gerar Pedidos de Compra</DialogTitle>
            <DialogDescription>
              Serão criados Pedidos de Compra para cada fornecedor com itens selecionados.
            </DialogDescription>
          </DialogHeader>

          {!qr.projectId && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Projeto *</p>
              <Select value={approveProjectId} onValueChange={setApproveProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar projeto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500">O projeto é necessário para criar os Pedidos de Compra.</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancelar</Button>
            <Button
              disabled={(!qr.projectId && !approveProjectId) || approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
            >
              {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar Aprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
